import { useSubscription } from '@trpc/tanstack-react-query'
import './cloud.css'
import { memo, useId, useState } from 'react'
import type { CloudControllerState } from '@companion-app/shared/Model/Cloud.js'
import { StaticAlert } from '~/Components/Alert.js'
import { Button } from '~/Components/Button'
import { Callout } from '~/Components/Callout.js'
import { FormLabel } from '~/Components/Form.js'
import { Grid } from '~/Components/Grid'
import { SwitchInputFieldWithLabel } from '~/Components/SwitchInputField.js'
import { LoadingRetryOrError } from '~/Resources/Loading.js'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import { CloudRegionPanel } from './RegionPanel.js'
import { CloudUserPass } from './UserPass.js'

export function CloudPage(): React.JSX.Element {
	const cloudState = useCloudState()

	return (
		<div className="cloud-region-panel">
			<h4>Companion Cloud</h4>

			<StaticAlert color="danger">
				This service is deprecated and will be removed in a future version of Companion. <br />
				We hope that before it is removed, an equivalent system will be made available in <i>Bitfocus Buttons</i> <br />
				As an alternative you can use the new <i>companion-satellite</i> module over a local network or vpn.
			</StaticAlert>
			<p>
				Access your Companion buttons from your Bitfocus Cloud account, or create a sophisticated network of Companion
				installations that work together over the internet for all your remote production needs.
			</p>
			<p>
				When enabled, Companion will make several persistent secure connections to different Bitfocus Cloud regions for
				redundancy. You can learn more about the service, the service provider and the safety of your data in the
				Companion Cloud documentation{' '}
				<a target="_blank" href="https://user.bitfocus.io/docs/companion-cloud">
					here
				</a>
				.
			</p>

			{cloudState ? (
				<CloudPageContent cloudState={cloudState} />
			) : (
				<LoadingRetryOrError dataReady={false} design="pulse" />
			)}
		</div>
	)
}

function useCloudState() {
	const [cloudState, setCloudState] = useState<CloudControllerState | null>(null)

	useSubscription(
		trpc.cloud.watchState.subscriptionOptions(undefined, {
			onStarted: () => {
				console.log('Started cloud state subscription')
				setCloudState(null)
			},
			onData: (newState) => {
				setCloudState(newState)
			},
			onError: (err) => {
				console.error('Error in cloud state subscription', err)
				setCloudState(null)
			},
		})
	)

	return cloudState
}

function CloudPageContent({ cloudState }: { cloudState: CloudControllerState }) {
	return (
		<>
			{!!cloudState.error && <StaticAlert color="danger">{cloudState.error}</StaticAlert>}

			{!cloudState.authenticated ? (
				<CloudUserPass
					working={cloudState.authenticating}
					username={cloudState.authenticatedAs}
					onClearError={() => {
						// TODO: reimplement
						// this.setState({ error: null })
					}}
				/>
			) : (
				<>
					<AuthState
						authenticatedAs={cloudState.authenticatedAs}
						cloudActive={cloudState.cloudActive}
						clearError={() => {
							// TODO: reimplement
							// this.setState({ error: null })
						}}
					/>

					<RegionsList
						regionIds={cloudState.regions || []}
						cloudActive={cloudState.cloudActive}
						canActivate={cloudState.canActivate}
					/>

					<SecretKeyPanel uuid={cloudState.uuid} />
				</>
			)}
		</>
	)
}

interface AuthStateProps {
	authenticatedAs: string | undefined
	cloudActive: boolean
	clearError: () => void
}

function AuthState({ authenticatedAs, cloudActive, clearError }: AuthStateProps) {
	const logoutMutation = useMutationExt(trpc.cloud.logout.mutationOptions())

	const userId = useId()

	return (
		<Grid.Col sm={6} className="cloud-auth-state">
			<FormLabel htmlFor={userId} className="mb-1">
				Logged in as
			</FormLabel>
			<div id={userId} className="text-muted px-4">
				{authenticatedAs}
			</div>

			{!cloudActive && (
				<div className="my-4">
					<Button
						color="success"
						onClick={() => {
							clearError()
							logoutMutation.mutate()
						}}
					>
						Log out
					</Button>
				</div>
			)}
		</Grid.Col>
	)
}

interface RegionsListProps {
	regionIds: string[]
	cloudActive: boolean
	canActivate: boolean
}

function RegionsList({ regionIds, cloudActive, canActivate }: RegionsListProps) {
	const setCloudActiveMutation = useMutationExt(trpc.cloud.setCloudActive.mutationOptions())

	return (
		<Grid.Col sm={12}>
			<div className="cloud-regions-section">
				<h5 className="cloud-regions-heading">Cloud regions</h5>

				{!cloudActive && (
					<div className="mb-4">
						Please select the regions that is closest to you. You need to select at least <b>two regions</b> which will
						give you redundancy.
					</div>
				)}

				<div className="cloud-region-list">
					{regionIds.map((regionId) => (
						<CloudRegionPanel key={regionId} hideDisabled={cloudActive} regionId={regionId} />
					))}
				</div>

				<div>
					{cloudActive && (
						<Callout color="info">Companion Cloud is currently activated. Deactivate to change regions.</Callout>
					)}

					<SwitchInputFieldWithLabel
						label="Activate Companion Cloud"
						disabled={!cloudActive && !canActivate}
						value={cloudActive}
						setValue={(val) => setCloudActiveMutation.mutate({ active: !!val })}
						small
						tooltip="Activate Companion Cloud"
					/>
				</div>
			</div>
		</Grid.Col>
	)
}

const SecretKeyPanel = memo(function SecretKeyPanel({ uuid }: { uuid: string }) {
	const regenerateUUIDMutation = useMutationExt(trpc.cloud.regenerateUUID.mutationOptions())

	return (
		<Grid.Col sm={12} className="super-secret-key">
			<h5>Super secret key</h5>

			<p>
				When you have successfully connected to two or more regions, you can use this key in another remote companion to
				control this companion. Go to the connections tab in another companion and search for "companion cloud", and add
				it with the key above to start controlling this companion via internet.
			</p>

			<StaticAlert color="success">{uuid}</StaticAlert>

			<div className="my-4">
				<Button color="primary" onClick={() => regenerateUUIDMutation.mutate()}>
					Regenerate secret key
				</Button>
			</div>
		</Grid.Col>
	)
})
